import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { axiosGetV3 } from '../../api/axios-handler';
import { EligiblePeersona, useEligiblePeersonas } from './useEligiblePeersonas';
import { peersonaKeys, PeersonaThreadSummary } from './usePeersonaChat';

/** Each past thread becomes its own row — no per-Peersona dedupe. */
export interface RecentConversationRow {
  peersona: EligiblePeersona;
  thread: PeersonaThreadSummary;
}

/**
 * Pull every thread for a Peersona. Returns the same ``PeersonaThreadSummary[]``
 * shape as ``usePeersonaThreads`` so they can share the React Query cache
 * key without colliding.
 */
async function fetchThreads(chatbotId: number): Promise<PeersonaThreadSummary[]> {
  try {
    const res = await axiosGetV3(`/peersona/${chatbotId}/threads`, { limit: 50 });
    return (res?.data?.threads ?? []) as PeersonaThreadSummary[];
  } catch {
    return [];
  }
}

function compareByUpdatedAtDesc(a: RecentConversationRow, b: RecentConversationRow): number {
  const aTime = a.thread.updated_at ? new Date(a.thread.updated_at).getTime() : 0;
  const bTime = b.thread.updated_at ? new Date(b.thread.updated_at).getTime() : 0;
  return bTime - aTime;
}

export interface UseRecentConversationsResult {
  isLoading: boolean;
  rows: RecentConversationRow[];
}

/**
 * Fan out across eligible Peersonas, flatten the threads into one list,
 * sort by ``updated_at`` desc. Collapses to a single request once the
 * backend ships ``GET /peersona/threads?listener_role_ids=…``.
 */
export function useRecentConversations(): UseRecentConversationsResult {
  const { isLoading: peersonasLoading, peersonas } = useEligiblePeersonas();

  const threadQueries = useQueries({
    queries: peersonas.map((p) => ({
      queryKey: peersonaKeys.threads(p.chatbotId),
      queryFn: () => fetchThreads(p.chatbotId),
      enabled: p.chatbotId != null,
      staleTime: 30_000,
    })),
  });

  const isLoading = peersonasLoading || threadQueries.some((q) => q.isLoading || q.isFetching);

  const rows = useMemo<RecentConversationRow[]>(() => {
    const flat: RecentConversationRow[] = [];
    peersonas.forEach((peersona, idx) => {
      const threads = (threadQueries[idx]?.data as PeersonaThreadSummary[] | undefined) ?? [];
      for (const thread of threads) {
        flat.push({ peersona, thread });
      }
    });
    flat.sort(compareByUpdatedAtDesc);
    return flat;
  }, [peersonas, threadQueries]);

  return { isLoading, rows };
}
